import { FC, useContext, useMemo, useState } from 'react';

import { Button, ITabOption, Table, Tabs } from '@smartshell/ui-kit';
import cn from 'classnames';
import { Column } from 'react-table';

import { AuthContext } from 'auth';
import { useGameAccounts } from 'hooks';
import { LocalizationContext } from 'localization';
import { TGameAccount, TGameAccountsGroup } from 'types';

import s from './GameAccountsBlock.module.scss';

type TTab = 'GROUPS' | 'BUSY_ACCOUNTS';

export const GameAccountsBlock: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const TABS: Array<ITabOption<TTab>> = [
    { title: t('accounts.groups.title'), value: 'GROUPS' },
    { title: t('accounts.busy.title'), value: 'BUSY_ACCOUNTS' },
  ];

  const GAME_ACCOUNTS_COLUMNS: Array<Column<TGameAccountsGroup>> = useMemo(
    () => [
      {
        Header: t('accounts.group.name'),
        accessor: 'title',
      },
      {
        id: 'free',
        Header: t('accounts.counter.free'),
        accessor: (group) =>
          group.accounts.filter((account) => !account.busy).length,
        Cell: ({ value: freeAccounts }: { value: number }) => (
          <span className={cn(s.label, s.free)}>{freeAccounts}</span>
        ),
        disableResizing: true,
        width: 150,
      },
      {
        id: 'busy',
        Header: t('accounts.counter.busy'),
        accessor: (group) =>
          group.accounts.filter((account) => account.busy).length,
        Cell: ({ value: busyAccounts }: { value: number }) => (
          <span className={cn(s.label, s.busy)}>{busyAccounts}</span>
        ),
        disableResizing: true,
        width: 140,
      },
    ],
    [t]
  );

  const [tab, setTab] = useState<TTab>('BUSY_ACCOUNTS');

  const { accountsGroups, freeAccount } = useGameAccounts(permissions);

  const busyAccountsColumns = useMemo<Array<Column<TGameAccount>>>(
    () => [
      {
        id: 'host',
        Header: t('accounts.pc'),
        accessor: (account) => account.host?.alias,
      },
      {
        id: 'group',
        Header: t('accounts.group'),
        accessor: (account) => account.group.title,
        Cell: ({ value: title }: { value: string }) => (
          <span className={s['account-group-title']}>{title}</span>
        ),
      },
      {
        Header: t('status'),
        accessor: 'busy',
        Cell: () => (
          <span className={cn(s.label, s.busy, s.status)}>
            {t('account.status.busy')}
          </span>
        ),
        width: 125,
        disableResizing: true,
        disableSortBy: true,
      },
      {
        Header: t('accounts.control'),
        accessor: 'id',
        Cell: ({ value: id }) => (
          <Button
            onClick={() => freeAccount(id)}
            variant="outline"
            className={s['free-button']}
          >
            {t('accounts.free')}
          </Button>
        ),
        disableSortBy: true,
        width: 170,
        disableResizing: true,
      },
    ],
    [freeAccount]
  );

  return (
    <div className={s.container}>
      {tab === 'GROUPS' && (
        <Table
          headerBlock={
            <Tabs
              options={TABS}
              value={tab}
              onChange={setTab}
              className={s.tabs}
            />
          }
          data={accountsGroups}
          columns={GAME_ACCOUNTS_COLUMNS}
          emptyDataPlaceholder={t('accounts.groups.placeholder')}
          className={s.table}
        />
      )}
      {tab === 'BUSY_ACCOUNTS' && (
        <Table
          headerBlock={
            <Tabs
              options={TABS}
              value={tab}
              onChange={setTab}
              className={s.tabs}
            />
          }
          data={accountsGroups
            .flatMap((group) => group.accounts)
            .filter((account) => account.busy)}
          columns={busyAccountsColumns}
          emptyDataPlaceholder={t('accounts.busy.placeholder')}
          className={s.table}
        />
      )}
    </div>
  );
};
